import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Registration
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <SecurityIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h1" 
            color="primary"
            sx={{ fontWeight: 'bold' }}
          >
            Privacy Policy
          </Typography>
        </Box>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          RoboJackets K12 Foundation - FTC Robotics League
        </Typography>
      </Box>

      {/* Privacy Policy Content */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          {/* Effective Date */}
          <Box sx={{ mb: 4, p: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Effective Date: July 27, 2025
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              The RoboJackets K12 Foundation ("we," "us," or "our") is committed to protecting the privacy of individuals involved in our FTC Robotics League. This Privacy Policy explains what information we collect, how we use it, and your choices regarding that information.
            </Typography>
          </Box>

          {/* Section 1: Information We Collect */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We collect personal information from participants, parents/guardians, mentors, and volunteers ("you") to operate and promote the FTC Robotics League and our related programs. The types of information we may collect include:
            </Typography>
            
            <Box sx={{ ml: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Identity Information:
              </Typography>
              <List dense sx={{ ml: 2 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• First Name" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Last Name" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Middle Name" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Preferred Name" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Gender" />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                Contact Information:
              </Typography>
              <List dense sx={{ ml: 2 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Email Address" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Phone Number" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Street Address" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• City" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Zip Code" />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                Educational Information:
              </Typography>
              <List dense sx={{ ml: 2 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• School District" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• School Name" />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• Graduation Year" />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
                Event-Related Information:
              </Typography>
              <List dense sx={{ ml: 2 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="• T-shirt Size" />
                </ListItem>
              </List>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: How We Use Your Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We use the information we collect solely for the following purposes:
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              To Operate the FTC Robotics League:
            </Typography>
            <List dense sx={{ ml: 2 }}>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Registration and team management" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Communication regarding league activities, schedules, and events" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Team and individual identification for competitions and awards" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Logistics and planning for league events (e.g., venue arrangements, materials)" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Ensuring participant safety and well-being" />
              </ListItem>
            </List>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              To Promote the FTC Robotics League and Sister Programs:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              • Sharing general league news and updates<br />
              • Promoting the RoboJackets K12 Foundation's sister programs, including our FRC (FIRST Robotics Competition) team and summer camps. This may include sending information about upcoming events, registration, or opportunities related to these programs.<br />
              • Creating promotional materials (e.g., photographs or videos from events, with appropriate consent as outlined below)
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Administrative and Internal Purposes:
            </Typography>
            <List dense sx={{ ml: 2 }}>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Internal record-keeping" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Statistical analysis to improve our programs" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="• Complying with legal obligations" />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: How We Share Your Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              3. How We Share Your Information
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We will not sell, rent, or lease your personal information to third parties. We may share your information only in the following limited circumstances:
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              With FIRST (For Inspiration and Recognition of Science and Technology):
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              As an official FTC League, we are required to share certain participant data with FIRST for registration, competition management, and program oversight. FIRST's privacy policy will govern their use of this information.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Service Providers:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              We may share information with trusted third-party service providers who assist us in operating our programs (e.g., event management software, communication platforms). These service providers are contractually obligated to protect your information and only use it for the purposes for which we disclose it to them.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Law Enforcement and Legal Requests:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court order or government agency request).
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              With Consent:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              We may share your information with your explicit consent for specific purposes not outlined in this policy.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Data Security */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, use, disclosure, alteration, or destruction. However, no internet transmission or electronic storage method is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: Your Choices and Rights */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              5. Your Choices and Rights
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Access and Correction:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              You may request access to the personal information we hold about you and request corrections to any inaccuracies.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Opt-Out of Promotional Communications:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              You can opt-out of receiving promotional emails from us by following the unsubscribe instructions provided in those emails. Please note that even if you opt-out of promotional communications, we may still send you non-promotional messages related to your participation in the league.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 3 }}>
              Image and Likeness:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, ml: 2 }}>
              We may capture photographs and videos at league events for promotional and archival purposes. If you do not wish for your image or your child's image to be used in such materials, please notify us in writing. Specific consent forms for media release may be required at registration.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: Children's Privacy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              6. Children's Privacy
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Our FTC Robotics League programs are primarily designed for students. We are committed to complying with applicable laws regarding children's online privacy, such as the Children's Online Privacy Protection Act (COPPA) where applicable.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              For participants under the age of 13, we require parental or guardian consent for the collection and use of personal information.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              Parents/guardians have the right to review their child's personal information, request its deletion, and refuse to permit its further collection or use. Please contact us to exercise these rights.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 7: Changes to This Privacy Policy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              7. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will post any changes on this page and update the "Effective Date" at the top of the policy. We encourage you to review this Privacy Policy periodically.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 8: Contact Us */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              8. Contact Us
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </Typography>
            
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'grey.50', 
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.200'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                RoboJackets K12 Foundation
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Email: info@robojacketsk12.org
              </Typography>
              <Typography variant="body1">
                Address: P.O. Box 214835 Auburn Hills, MI 48321-4835
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy; 